/**
 * CWS-1882 ("one sale = one order"): redeeming a prepaid wash no longer creates an order.
 * `POST /order` with `wash_type: 'prepaid_wash_redemption'` now answers with the PURCHASE
 * order (`prepaid_wash_book`) carrying a `prepaid_redemption` block that describes the visit
 * that was recorded. `reused: true` means the backend matched a visit recorded moments ago
 * (same car, same book, inside a 10-minute window) — no extra wash was drawn and no tunnel
 * row or gate action happened, so the UI must say so instead of claiming a fresh redemption.
 */

/** The `prepaid_redemption` block the API returns on the purchase order. */
export interface PrepaidRedemptionVisit {
  visit_id: string;
  prepaid_purchase_id: string;
  purchase_order_id: string;
  redeemed_count: number;
  redemptions: Record<string, any>[];
  reused: boolean;
  lpr_entry_id: string | null;
  addon_order_id: string | null;
}

/** Tolerant view of the block: older backends omit it, newer ones may add fields. */
export type PrepaidRedemptionVisitLike = Partial<PrepaidRedemptionVisit> & Record<string, any>;

/** Copy a component binds after a prepaid redemption round-trip. */
export interface PrepaidRedemptionResultDisplay {
  reused: boolean;
  redeemedCount: number;
  title: string;
  message: string;
}

const REUSED_TITLE = 'Already redeemed';
const REUSED_MESSAGE = 'This wash was redeemed a moment ago; nothing more was used.';
/** The heading a successful redemption has always shown; only a reused visit changes it. */
const REDEEMED_TITLE = 'Prepaid Redeemed Successfully!';
const UNKNOWN_COUNT_MESSAGE = 'The wash was redeemed from the prepaid book.';

function toCount(value: unknown): number {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return parsed;
}

/**
 * Turns the API's `prepaid_redemption` block into the wording the staff app shows.
 * A fresh redemption keeps the heading the success modal has always had and says how many
 * washes were drawn in the note; only a reused visit changes the heading.
 * `fallbackCount` is the count the UI asked for (the sum of the redemption lines posted),
 * used when the API did not report one — an older backend, or a response without the block.
 */
export function describePrepaidRedemptionResult(
  prepaidRedemption: PrepaidRedemptionVisitLike | null | undefined,
  fallbackCount = 0
): PrepaidRedemptionResultDisplay {
  const reused = prepaidRedemption?.['reused'] === true;
  const redeemedCount = toCount(prepaidRedemption?.['redeemed_count']) || toCount(fallbackCount);

  if (reused) {
    return {
      reused: true,
      redeemedCount,
      title: REUSED_TITLE,
      message: REUSED_MESSAGE,
    };
  }

  if (redeemedCount <= 0) {
    return {
      reused: false,
      redeemedCount: 0,
      title: REDEEMED_TITLE,
      message: UNKNOWN_COUNT_MESSAGE,
    };
  }

  const noun = redeemedCount === 1 ? 'wash' : 'washes';
  return {
    reused: false,
    redeemedCount,
    title: REDEEMED_TITLE,
    message: `${redeemedCount} ${noun} redeemed from the prepaid book.`,
  };
}

/**
 * The order id to use after a redemption: the purchase order the visit belongs to.
 * Falls back to the responding order's own id (they are the same order today) and then
 * to whatever the caller already had, so an older backend keeps working unchanged.
 */
export function resolvePrepaidRedemptionOrderId(
  prepaidRedemption: PrepaidRedemptionVisitLike | null | undefined,
  fallbackOrderId?: string | null
): string {
  const purchaseOrderId = String(prepaidRedemption?.['purchase_order_id'] ?? '').trim();
  if (purchaseOrderId) {
    return purchaseOrderId;
  }
  return String(fallbackOrderId ?? '').trim();
}

/**
 * A reused visit drew no wash and stamped nothing new, so the staff app must not re-stamp
 * the tunnel LPR entry for it (the backend already stamped it on the original visit).
 */
export function shouldUpdateLprEntryAfterPrepaidRedemption(
  prepaidRedemption: PrepaidRedemptionVisitLike | null | undefined
): boolean {
  return prepaidRedemption?.['reused'] !== true;
}
